import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, MapPin } from 'lucide-react';
import BrandLogo from './BrandLogo';

const WORKSHOP_LOGO = 'https://customer-assets.emergentagent.com/job_eduai-portal-6/artifacts/zrncz5al_Untitled%20%281000%20x%20500%20px%29%20%281%29.jpg';

const FLAGSHIP_COURSES = [
  { to: '/performance-marketing',                       label: 'Performance Marketing Course' },
  { to: '/ai-bootcamp',                                 label: 'AI Bootcamp for School Students' },
  { to: '/film-making-animation-course',                label: 'Film Making & Cinematic Animation' },
  { to: '/game-development-course',                     label: 'Game Development Course' },
  { to: '/jewellery-designing-course',                  label: 'Jewellery Designing in Blender' },
  { to: '/complete-blender-character-design-course',    label: 'Blender & Character Design' },
];

const SHORT_WORKSHOPS = [
  { to: '/workshops/photo-editing-ai',          label: 'Photo Editing with AI' },
  { to: '/workshops/video-editing-reels',       label: 'Video Editing & Reels' },
  { to: '/workshops/social-media-booster',      label: 'Social Media Booster' },
  { to: '/workshops/linkedin-personal-branding',label: 'LinkedIn Personal Branding' },
  { to: '/workshops/prompt-engineering',        label: 'Prompt Engineering' },
  { to: '/workshops/canva-design-secrets',      label: 'Canva Design Secrets' },
  { to: '/workshops/freelancing-side-income',   label: 'Freelancing & Side Income' },
  { to: '/workshops/digital-ads-automation',    label: 'Digital Ads Automation' },
  { to: '/workshops/content-creation-mastery',  label: 'Content Creation Mastery' },
  { to: '/workshops/resume-portfolio-builder',  label: 'Resume & Portfolio Builder' },
];

const QUICK_LINKS = [
  { to: '/',                                            label: 'Home' },
  { to: '/book-demo',                                   label: 'Book a Demo' },
  { to: '/csdgpm-offer',                                label: 'CSDGPM Special Offer' },
  { to: '/courses/csdgpm',                              label: 'CSDGPM Programme' },
  { to: '/enrollment',                                  label: 'Enrollment' },
  { to: '/thank-you',                                   label: 'Thank You' },
];

const CITIES = ['Jaipur', 'Noida', 'Bangalore', 'Dubai', 'US'];

const SiteFooter = ({ accent = 'orange' }) => {
  const accentRing = {
    orange:  'border-orange-500/30  text-orange-300',
    emerald: 'border-emerald-500/30 text-emerald-300',
    amber:   'border-amber-500/30   text-amber-300',
    violet:  'border-violet-500/30  text-violet-300',
    blue:    'border-blue-500/30    text-blue-300',
    yellow:  'border-amber-500/30   text-amber-300',
    purple:  'border-violet-500/30  text-violet-300',
  }[accent] || 'border-orange-500/30 text-orange-300';

  return (
    <footer
      data-testid="site-footer"
      className={`bg-slate-950 text-slate-300 border-t-2 ${accentRing.split(' ')[0]}`}
    >
      <div className="container mx-auto max-w-7xl px-6 py-14">
        {/* Top — branding + cities */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <div className="mb-4">
              <BrandLogo size="lg" variant="white" linkTo="/" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5 max-w-sm">
              Premium, job-oriented AI &amp; digital training — built for the next generation of creators, marketers and engineers.
            </p>

            {/* Powered by Workshop Library */}
            <div className="flex items-center gap-3 mb-6" data-testid="footer-powered-by">
              <span className="text-xs uppercase tracking-widest text-slate-500">Powered by</span>
              <a href="https://workshoplibrary.com" target="_blank" rel="noopener noreferrer">
                <img
                  src={WORKSHOP_LOGO}
                  alt="Workshop Library"
                  className="h-10 md:h-12 object-contain rounded shadow-md shadow-amber-500/10" decoding="async" fetchPriority="high" loading="eager" />
              </a>
            </div>

            {/* Cities */}
            <div className="space-y-2" data-testid="footer-cities">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <MapPin className="w-3.5 h-3.5" /> Global Presence
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-2 text-sm">
                {CITIES.map((c, i) => (
                  <React.Fragment key={c}>
                    <span className={`font-semibold ${accentRing.split(' ')[1]}`}>{c}</span>
                    {i < CITIES.length - 1 && <span className="text-slate-700">•</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Flagship Courses */}
          <div className="lg:col-span-3" data-testid="footer-flagship-courses">
            <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-4">Flagship Courses</h4>
            <ul className="space-y-2.5">
              {FLAGSHIP_COURSES.map((c) => (
                <li key={c.to}>
                  <Link to={c.to} className="text-sm text-slate-400 hover:text-white transition-colors">{c.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Short Workshops */}
          <div className="lg:col-span-3" data-testid="footer-workshops">
            <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-4">Short Workshops</h4>
            <ul className="space-y-2.5">
              {SHORT_WORKSHOPS.map((c) => (
                <li key={c.to}>
                  <Link to={c.to} className="text-sm text-slate-400 hover:text-white transition-colors">{c.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links + contact */}
          <div className="lg:col-span-2" data-testid="footer-quick-links">
            <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-4">Quick Links</h4>
            <ul className="space-y-2.5 mb-6">
              {QUICK_LINKS.map((c) => (
                <li key={c.to}>
                  <Link to={c.to} className="text-sm text-slate-400 hover:text-white transition-colors">{c.label}</Link>
                </li>
              ))}
            </ul>

            <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-3">Get in Touch</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://wa.me/919891550666?text=Hi,%20I%20want%20to%20know%20more%20about%20your%20courses"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-400 hover:text-green-300"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us
                </a>
              </li>
              <li>
                <a href="mailto:team@aischoolforall.com" className="inline-flex items-center gap-2 text-slate-400 hover:text-white">
                  <Mail className="w-4 h-4" /> team@aischoolforall.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>&copy; {new Date().getFullYear()} AI School for All &middot; Powered by Workshop Library &middot; All rights reserved.</p>
          <div className="flex gap-5">
            <a href="/privacy" className="hover:text-slate-300">Privacy</a>
            <a href="/terms" className="hover:text-slate-300">Terms</a>
            <a href="/refund" className="hover:text-slate-300">Refund</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
